/**
 * TwoFactorSection - the signed-in user's own TOTP enrollment.
 *
 * Enrollment is deliberately two-phase: `setup` hands back a secret that does
 * nothing until `activate` proves the authenticator produces a matching code.
 * A user who scans a QR and then loses their phone before confirming is not
 * locked out, because nothing was enforced yet.
 *
 * Recovery codes are shown exactly once. The server keeps only hashes, so this
 * panel is the sole opportunity to save them — the copy affordances are not
 * decoration.
 */
import { useCallback, useEffect, useState } from "react";

import { AuthClient, type TotpEnrollment, type TotpStatus } from "../../auth/AuthClient";
import { useAuthStore } from "../../auth/authStore";
import { QrCode } from "../../auth/QrCode";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

type Phase =
  | { readonly kind: "loading" }
  | { readonly kind: "failed"; readonly message: string }
  | { readonly kind: "settled"; readonly status: TotpStatus }
  | { readonly kind: "enrolling"; readonly enrollment: TotpEnrollment }
  /** Terminal display of codes the server will never show again. */
  | { readonly kind: "codes"; readonly codes: ReadonlyArray<string> };

export function TwoFactorSection() {
  const signedIn = useAuthStore((state) => state.status === "authenticated");
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  const refresh = useCallback(async () => {
    try {
      setPhase({ kind: "settled", status: await AuthClient.totpStatus() });
    } catch (err) {
      setPhase({
        kind: "failed",
        message: err instanceof Error ? err.message : "Failed to load two-factor status",
      });
    }
  }, []);

  useEffect(() => {
    // With `authEnabled: false` there is no account to attach a second factor
    // to, and every /auth/totp call answers 401 — say so instead of surfacing
    // "No session" as a failure.
    if (!signedIn) return;
    void refresh();
  }, [refresh, signedIn]);

  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const beginEnrollment = () =>
    run(async () => {
      const enrollment = await AuthClient.totpSetup();
      setCode("");
      setShowSecret(false);
      setPhase({ kind: "enrolling", enrollment });
    });

  const activate = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void run(async () => {
      const codes = await AuthClient.totpActivate(code.trim());
      setCode("");
      setPhase({ kind: "codes", codes });
    });
  };

  const disable = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void run(async () => {
      await AuthClient.totpDisable(password);
      setPassword("");
      setConfirmingDisable(false);
      await refresh();
    });
  };

  const regenerate = () =>
    run(async () => {
      setPhase({ kind: "codes", codes: await AuthClient.totpRegenerateRecoveryCodes() });
    });

  const errorBanner = error ? (
    <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
      {error}
    </div>
  ) : null;

  const heading = (
    <div className="min-w-0">
      <h3 className="text-base font-semibold">Two-factor authentication</h3>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Requires a 6-digit code from an authenticator app (Google Authenticator, 1Password, Authy)
        in addition to your password.
      </p>
    </div>
  );

  if (!signedIn) {
    return (
      <div className="flex flex-col gap-4">
        {heading}
        <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
          This server is running without authentication, so there is no account to protect. Enable
          auth on the server to use two-factor authentication.
        </div>
      </div>
    );
  }

  if (phase.kind === "loading") {
    return (
      <div className="flex flex-col gap-4">
        {heading}
        <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
          Loading…
        </div>
      </div>
    );
  }

  if (phase.kind === "failed") {
    return (
      <div className="flex flex-col gap-4">
        {heading}
        <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {phase.message}
        </div>
      </div>
    );
  }

  if (phase.kind === "codes") {
    const asText = phase.codes.join("\n");
    return (
      <div className="flex flex-col gap-4">
        {heading}
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4">
          <h4 className="text-sm font-semibold text-amber-200">Save your recovery codes</h4>
          <p className="mt-1 text-xs text-amber-200/80">
            Each one signs you in once if you lose access to your authenticator. They are not stored
            in a form we can show again — copy them somewhere safe now.
          </p>
          <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-sm">
            {phase.codes.map((recoveryCode) => (
              <li key={recoveryCode}>{recoveryCode}</li>
            ))}
          </ul>
          <div className="mt-4 flex items-center gap-2">
            <Button type="button" size="sm" onClick={() => copyToClipboard(asText)}>
              {isCopied ? "Copied" : "Copy all"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                void refresh();
              }}
            >
              I saved them
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (phase.kind === "enrolling") {
    return (
      <div className="flex flex-col gap-4">
        {heading}
        <div className="rounded-md border border-border/60 bg-card/40 p-6">
          <ol className="flex flex-col gap-5 text-sm">
            <li>
              <p className="font-medium">1. Scan this code with your authenticator app</p>
              <div className="mt-3 flex flex-wrap items-start gap-5">
                <QrCode
                  value={phase.enrollment.otpauthUri}
                  title="Two-factor setup QR code"
                  className="h-44 w-44 rounded bg-white p-1"
                />
                <div className="min-w-0 flex-1 text-xs text-muted-foreground">
                  <p>Can&apos;t scan? Enter this key manually instead.</p>
                  {showSecret ? (
                    <p className="mt-2 font-mono text-sm break-all text-foreground">
                      {phase.enrollment.secret}
                    </p>
                  ) : null}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowSecret((previous) => !previous)}
                    >
                      {showSecret ? "Hide key" : "Show key"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(phase.enrollment.secret)}
                    >
                      {isCopied ? "Copied" : "Copy key"}
                    </Button>
                  </div>
                </div>
              </div>
            </li>
            <li>
              <p className="font-medium">2. Enter the code it shows</p>
              <form onSubmit={activate} className="mt-3 flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="totp-activate">Code</Label>
                  <Input
                    id="totp-activate"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    placeholder="123456"
                    maxLength={6}
                    className="max-w-40 font-mono tracking-[0.3em]"
                    required
                  />
                </div>
                {errorBanner}
                <div className="flex gap-2">
                  <Button type="submit" size="sm" disabled={busy}>
                    {busy ? "Verifying…" : "Turn on two-factor"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      setError(null);
                      void refresh();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </li>
          </ol>
        </div>
      </div>
    );
  }

  const { status } = phase;

  return (
    <div className="flex flex-col gap-4">
      {heading}
      <div className="rounded-md border border-border/60 bg-card/40 p-6">
        {status.activated ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
              <span className="font-medium">Two-factor authentication is on</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {status.remainingRecoveryCodes} unused recovery{" "}
              {status.remainingRecoveryCodes === 1 ? "code" : "codes"} remaining. Regenerating
              replaces every existing code.
            </p>
            {errorBanner}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={regenerate}
              >
                Regenerate recovery codes
              </Button>
              {confirmingDisable ? null : (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    setError(null);
                    setConfirmingDisable(true);
                  }}
                >
                  Turn off
                </Button>
              )}
            </div>

            {confirmingDisable ? (
              // Requires the password, not just an open session: an unattended
              // browser must not be enough to strip the second factor.
              <form
                onSubmit={disable}
                className="flex flex-col gap-3 border-t border-border/60 pt-4"
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="totp-disable-password">
                    Confirm your password to turn off two-factor
                  </Label>
                  <Input
                    id="totp-disable-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="max-w-xs"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" size="sm" variant="destructive" disabled={busy}>
                    {busy ? "Turning off…" : "Turn off two-factor"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      setConfirmingDisable(false);
                      setPassword("");
                      setError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="h-2 w-2 rounded-full bg-muted-foreground/60" aria-hidden />
              <span className="font-medium">Two-factor authentication is off</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {status.pendingSetup
                ? "You started setting this up but never confirmed a code. Starting again issues a new key."
                : "Your password alone signs you in. Adding a second factor is strongly recommended for accounts reachable from the internet."}
            </p>
            {errorBanner}
            <div>
              <Button type="button" size="sm" disabled={busy} onClick={beginEnrollment}>
                {busy ? "Preparing…" : status.pendingSetup ? "Start again" : "Set up"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
